import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useTheme } from '../ThemeContext';

// ============================================================
// TYPES
// ============================================================
interface RawBin {
  WAREHOUSE: string; STORAGE_AREA: string; SHELF_SIZE: string;
  NUMBER_OF_LEVELS: number; TOTAL_NO_OF_SEGMENTS: number; FREE_SEGMENTS: number;
  LPFSTA?: string; OCCUPANCY_STATUS?: string; SOURCE?: string;
}
interface CustomerStorageItem {
  WAREHOUSE: string; CUSTOMER: string; STORAGE_LOCATION: string;
  STORAGE_TYPE: string; USED_PALLETS: number; TOTAL_CAPACITY: number; AVAILABLE_CAPACITY: number;
}
interface ManualEntry {
  warehouse: string; warehouse_area: string; client_name: string;
  closing_balance: number; capacity: number; area_capacity: number;
  week_number: number; year: number; uom: string;
}
interface BlockedItem {
  WAREHOUSE: string; STORAGE_AREA?: string; STORAGE_LOCATION?: string;
  CUSTOMER?: string; BLOCKED_BINS?: number; BLOCKED_PALLETS?: number; TOTAL_NO_OF_SEGMENTS?: number;
}
interface Props {
  rawWarehouseData: RawBin[]; rawVirtualData: RawBin[];
  customerStorageData: CustomerStorageItem[]; manualEntries: ManualEntry[];
  areaCustomerBlockedData: BlockedItem[];
}

type BinType = 'RACK_SMALL' | 'RACK_LARGE' | 'FLOOR_BULK' | 'TEMP_BLOCKED' | 'VIRTUAL';

interface BinRow {
  id: string; warehouse: string; storageArea: string; shelfSize: string;
  level: number; totalSegments: number; freeSegments: number;
  palletCapacity: number; usedPallets: number; freePallets: number;
  status: string; binType: BinType; originalBinType: BinType;
  isLevel10: boolean; excluded: boolean; overridden: boolean;
  overrideCapacity?: number;
}

// Persisted user changes
interface UserChanges {
  excludedBins: string[];
  // binId -> { newType, customCapacity }
  overrides: Record<string, { newType: BinType; customCap?: number }>;
  threshold: number;
  savedAt: string;
}

const STORAGE_KEY = 'dcv_user_changes';

// ============================================================
// HELPERS
// ============================================================
// Cap: seg 1-2 → as-is, seg 3-9 + level=10 → 2, seg 3-9 + level≠10 → 1, seg ≥10 → as-is.
const calcPalletCap = (seg: number, level: number, thr: number): number => {
  if (seg <= 0) return 0;
  if (seg <= 2) return seg;
  if (seg < thr) return level === 10 ? 2 : 1;
  return seg;
};
const calcUsedPal = (seg: number, free: number, _level: number, _thr: number): number => {
  if (seg === 0) return 0;
  return Math.max(0, seg - free);
};

const WH_NAMES: Record<string, string> = {
  'DPW': 'Dammam Port WH', 'DSI': 'Dammam 2nd', 'JDA': 'Jeddah Modon3A',
  'JDB': 'Jeddah 3B', 'JUB': 'Jubail WH', 'JUB_1': 'JUBAIL 1 WH',
  'JUB_2': 'JUBAIL 2', 'RUH': 'Hala Riyadh WH', 'RUH_M': 'HALA Riyadh WH'
};

const fmtDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// ============================================================
// COMPONENT
// ============================================================
const DynamicCapacityVerification: React.FC<Props> = ({
  rawWarehouseData, rawVirtualData, customerStorageData, manualEntries, areaCustomerBlockedData
}) => {
  const { colors } = useTheme();
  const reportRef = useRef<HTMLDivElement>(null);

  // Load saved changes
  const loadSaved = (): UserChanges | null => {
    try { const s = localStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
  };
  const saved = loadSaved();

  const [threshold, setThreshold] = useState(saved?.threshold || 4);
  const [selectedWarehouse, setSelectedWarehouse] = useState('ALL');
  const [viewMode, setViewMode] = useState<'overview' | 'rackSmall' | 'rackLarge' | 'floorBulk' | 'tempBlocked' | 'manual' | 'customers' | 'compare' | 'report'>('overview');
  const [expandedWh, setExpandedWh] = useState<string | null>(null);
  const [excludedBins, setExcludedBins] = useState<Set<string>>(new Set(saved?.excludedBins || []));
  const [binOverrides, setBinOverrides] = useState<Record<string, { newType: BinType; customCap?: number }>>(saved?.overrides || {});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'warehouse' | 'level' | 'capacity' | 'used' | 'status'>('warehouse');
  const [sortAsc, setSortAsc] = useState(true);
  const [tablePage, setTablePage] = useState(0);
  const [showCharts, setShowCharts] = useState(true);
  const [incVirtual, setIncVirtual] = useState(true);
  const [incManual, setIncManual] = useState(true);
  const [incFloorBulk, setIncFloorBulk] = useState(true);
  const [editingBin, setEditingBin] = useState<string | null>(null);
  const [editCap, setEditCap] = useState(0);
  const [editType, setEditType] = useState<BinType>('RACK_SMALL');
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [hasUnsaved, setHasUnsaved] = useState(false);

  // Auto-save indicator
  useEffect(() => {
    if (saved) {
      const excChanged = JSON.stringify(Array.from(excludedBins).sort()) !== JSON.stringify((saved.excludedBins || []).sort());
      const ovrChanged = JSON.stringify(binOverrides) !== JSON.stringify(saved.overrides || {});
      const thrChanged = threshold !== saved.threshold;
      setHasUnsaved(excChanged || ovrChanged || thrChanged);
    } else {
      setHasUnsaved(excludedBins.size > 0 || Object.keys(binOverrides).length > 0);
    }
  }, [excludedBins, binOverrides, threshold]);

  // Save to localStorage
  const saveChanges = useCallback(() => {
    try {
      const data: UserChanges = {
        excludedBins: Array.from(excludedBins),
        overrides: binOverrides,
        threshold,
        savedAt: fmtDate(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setSaveStatus(`Saved at ${fmtDate()}`);
      setHasUnsaved(false);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch {
      setSaveStatus('Save failed!');
    }
  }, [excludedBins, binOverrides, threshold]);

  // Clear saved
  const clearSaved = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setExcludedBins(new Set());
    setBinOverrides({});
    setSaveStatus('Cleared');
    setHasUnsaved(false);
    setTimeout(() => setSaveStatus(''), 2000);
  }, []);

  // Export as JSON
  const exportChanges = useCallback(() => {
    const data: UserChanges = { excludedBins: Array.from(excludedBins), overrides: binOverrides, threshold, savedAt: fmtDate() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `dcv-config-${fmtDate().replace(/[: ]/g, '-')}.json`; a.click();
    URL.revokeObjectURL(url);
  }, [excludedBins, binOverrides, threshold]);

  // ============================================================
  // BUILD ALL BINS (with overrides applied)
  // ============================================================
  const allBins: BinRow[] = useMemo(() => {
    const bins: BinRow[] = [];
    rawWarehouseData.forEach((item, idx) => {
      const seg = item.TOTAL_NO_OF_SEGMENTS || 0;
      const free = item.FREE_SEGMENTS || 0;
      const lvl = item.NUMBER_OF_LEVELS || 0;
      const blocked = String(item.LPFSTA || '').trim() === '2' || item.OCCUPANCY_STATUS === 'TEMP_BLOCKED';
      const isFloor = seg === 0;
      const id = `P-${item.WAREHOUSE}-${item.STORAGE_AREA}-${lvl}-${idx}`;
      let origType: BinType = 'RACK_SMALL';
      if (blocked) origType = 'TEMP_BLOCKED';
      else if (isFloor) origType = 'FLOOR_BULK';
      else if (seg > threshold) origType = 'RACK_LARGE';

      // Check override
      const ovr = binOverrides[id];
      const finalType = ovr ? ovr.newType : origType;
      let cap: number, used: number;
      if (ovr && ovr.customCap !== undefined) {
        cap = ovr.customCap;
        // Proportional used based on segment occupancy
        const occRatio = seg > 0 ? Math.max(0, (seg - free) / seg) : 0;
        used = Math.round(cap * occRatio);
      } else {
        cap = blocked ? calcPalletCap(seg, lvl, threshold) : calcPalletCap(seg, lvl, threshold);
        used = blocked ? cap : calcUsedPal(seg, free, lvl, threshold);
      }

      bins.push({
        id, warehouse: item.WAREHOUSE, storageArea: item.STORAGE_AREA,
        shelfSize: item.SHELF_SIZE || (isFloor ? 'FLOOR' : 'N/A'), level: lvl,
        totalSegments: seg, freeSegments: free,
        palletCapacity: cap, usedPallets: used, freePallets: Math.max(0, cap - used),
        status: blocked ? 'TEMP_BLOCKED' : isFloor ? 'FLOOR/BULK' : free === 0 ? 'FULL' : free === seg ? 'EMPTY' : 'PARTIAL',
        binType: finalType, originalBinType: origType,
        isLevel10: lvl === 10, excluded: excludedBins.has(id),
        overridden: !!ovr, overrideCapacity: ovr?.customCap,
      });
    });
    if (incVirtual && rawVirtualData) {
      rawVirtualData.forEach((item, idx) => {
        const seg = item.TOTAL_NO_OF_SEGMENTS || 0;
        const free = item.FREE_SEGMENTS || 0;
        const lvl = item.NUMBER_OF_LEVELS || 0;
        const id = `V-${item.WAREHOUSE}-${item.STORAGE_AREA}-${lvl}-${idx}`;
        const cap = calcPalletCap(seg, lvl, threshold);
        const used = calcUsedPal(seg, free, lvl, threshold);
        bins.push({
          id, warehouse: item.WAREHOUSE, storageArea: item.STORAGE_AREA,
          shelfSize: 'VIRTUAL', level: lvl, totalSegments: seg, freeSegments: free,
          palletCapacity: cap, usedPallets: used, freePallets: Math.max(0, cap - used),
          status: free === 0 ? 'FULL' : free === seg ? 'EMPTY' : 'PARTIAL',
          binType: 'VIRTUAL', originalBinType: 'VIRTUAL',
          isLevel10: lvl === 10, excluded: excludedBins.has(id), overridden: false,
        });
      });
    }
    return bins;
  }, [rawWarehouseData, rawVirtualData, threshold, excludedBins, binOverrides, incVirtual]);

  // ============================================================
  // AGGREGATIONS (identical to previous but now with overrides)
  // ============================================================
  const customerByWh = useMemo(() => {
    if (!customerStorageData) return {} as Record<string, { ibmPallets: number; manualPallets: number; manualCap: number; customers: string[] }>;
    const m: Record<string, { ibmPallets: number; manualPallets: number; manualCap: number; customers: string[] }> = {};
    customerStorageData.forEach(i => {
      const w = i.WAREHOUSE; if (!m[w]) m[w] = { ibmPallets: 0, manualPallets: 0, manualCap: 0, customers: [] };
      if (i.STORAGE_TYPE === 'IBM') m[w].ibmPallets += i.USED_PALLETS || 0;
      else if (i.STORAGE_TYPE === 'MANUAL') { m[w].manualPallets += i.USED_PALLETS || 0; m[w].manualCap += i.TOTAL_CAPACITY || 0; }
      if (i.CUSTOMER && !m[w].customers.includes(i.CUSTOMER)) m[w].customers.push(i.CUSTOMER);
    });
    return m;
  }, [customerStorageData]);

  const manualByWh = useMemo(() => {
    if (!manualEntries || !manualEntries.length) return {} as Record<string, { pallets: number; capacity: number; clients: string[]; entries: number }>;
    const m: Record<string, { pallets: number; capacity: number; clients: string[]; entries: number }> = {};
    const latest: Record<string, ManualEntry> = {};
    [...manualEntries].sort((a, b) => (b.year * 100 + b.week_number) - (a.year * 100 + a.week_number)).forEach(e => { const k = `${e.warehouse}::${e.client_name}`; if (!latest[k]) latest[k] = e; });
    Object.values(latest).forEach(e => {
      if (!m[e.warehouse]) m[e.warehouse] = { pallets: 0, capacity: 0, clients: [], entries: 0 };
      m[e.warehouse].pallets += e.closing_balance || 0; m[e.warehouse].capacity += e.area_capacity || e.capacity || 0;
      if (!m[e.warehouse].clients.includes(e.client_name)) m[e.warehouse].clients.push(e.client_name); m[e.warehouse].entries++;
    });
    return m;
  }, [manualEntries]);

  const blockedByWh = useMemo(() => {
    if (!areaCustomerBlockedData) return {} as Record<string, { bins: number; pallets: number }>;
    const m: Record<string, { bins: number; pallets: number }> = {};
    areaCustomerBlockedData.forEach(i => { const w = i.WAREHOUSE; if (!m[w]) m[w] = { bins: 0, pallets: 0 }; m[w].bins += i.BLOCKED_BINS || 1; m[w].pallets += i.BLOCKED_PALLETS || i.TOTAL_NO_OF_SEGMENTS || 0; });
    return m;
  }, [areaCustomerBlockedData]);

  // Filter
  const whFilter = (w: string) => selectedWarehouse === 'ALL' || w === selectedWarehouse;
  const filteredBins = useMemo(() => {
    let b = allBins.filter(x => !x.excluded && whFilter(x.warehouse));
    if (searchTerm) { const t = searchTerm.toLowerCase(); b = b.filter(x => x.storageArea.toLowerCase().includes(t) || x.warehouse.toLowerCase().includes(t) || x.shelfSize.toLowerCase().includes(t)); }
    return b;
  }, [allBins, selectedWarehouse, searchTerm]);

  const rackSmallBins = useMemo(() => filteredBins.filter(b => b.binType === 'RACK_SMALL'), [filteredBins]);
  const rackLargeBins = useMemo(() => filteredBins.filter(b => b.binType === 'RACK_LARGE'), [filteredBins]);
  const floorBulkBins = useMemo(() => filteredBins.filter(b => b.binType === 'FLOOR_BULK'), [filteredBins]);
  const tempBlockedArr = useMemo(() => filteredBins.filter(b => b.binType === 'TEMP_BLOCKED'), [filteredBins]);

  const warehouses = useMemo(() => {
    const s = new Set<string>();
    rawWarehouseData.forEach(d => s.add(d.WAREHOUSE));
    if (rawVirtualData) rawVirtualData.forEach(d => s.add(d.WAREHOUSE));
    if (manualEntries) manualEntries.forEach(d => { if (d.warehouse) s.add(d.warehouse); });
    if (customerStorageData) customerStorageData.forEach(d => { if (d.WAREHOUSE) s.add(d.WAREHOUSE); });
    return Array.from(s).sort();
  }, [rawWarehouseData, rawVirtualData, manualEntries, customerStorageData]);

  // Per-warehouse summary
  const whSummaries = useMemo(() => {
    return warehouses.filter(whFilter).map(wh => {
      const wb = allBins.filter(b => b.warehouse === wh && !b.excluded);
      const rS = wb.filter(b => b.binType === 'RACK_SMALL');
      const rL = wb.filter(b => b.binType === 'RACK_LARGE');
      const fl = wb.filter(b => b.binType === 'FLOOR_BULK');
      const bl = wb.filter(b => b.binType === 'TEMP_BLOCKED');
      const vi = wb.filter(b => b.binType === 'VIRTUAL');
      const l10 = wb.filter(b => b.isLevel10);
      const rCap = rS.reduce((s, b) => s + b.palletCapacity, 0) + rL.reduce((s, b) => s + b.palletCapacity, 0);
      const rUsed = rS.reduce((s, b) => s + b.usedPallets, 0) + rL.reduce((s, b) => s + b.usedPallets, 0);
      const blCap = bl.reduce((s, b) => s + b.palletCapacity, 0);
      const vUsed = vi.reduce((s, b) => s + b.usedPallets, 0);
      const cust = customerByWh[wh] || { ibmPallets: 0, manualPallets: 0, manualCap: 0, customers: [] };
      const man = incManual ? (manualByWh[wh] || { pallets: 0, capacity: 0, clients: [] as string[], entries: 0 }) : { pallets: 0, capacity: 0, clients: [] as string[], entries: 0 };
      const flPallets = incFloorBulk ? Math.max(0, cust.ibmPallets - rUsed - blCap) : 0;
      const tCap = rCap + blCap + (incManual ? man.capacity : 0);
      const tUsed = rUsed + blCap + vUsed + (incManual ? man.pallets : 0) + (incFloorBulk ? flPallets : 0);
      const overrideCount = wb.filter(b => b.overridden).length;
      return {
        warehouse: wh, displayName: WH_NAMES[wh] || wh,
        rackSmallBins: rS.length, rackLargeBins: rL.length, floorBulkBins: fl.length,
        tempBlockedBins: bl.length, virtualBins: vi.length, level10Count: l10.length,
        rackCap: rCap, rackUsed: rUsed, blCap, vUsed, flPallets,
        ibmPallets: cust.ibmPallets, ibmCustomers: cust.customers.length,
        manPallets: man.pallets, manCap: man.capacity,
        tCap, tUsed, tFree: Math.max(0, tCap - tUsed),
        utilPct: tCap > 0 ? Math.round((tUsed / tCap) * 100) : 0,
        overrideCount,
      };
    }).filter(w => w.rackSmallBins + w.rackLargeBins + w.floorBulkBins + w.tempBlockedBins + w.virtualBins + w.manPallets + w.ibmPallets > 0)
      .sort((a, b) => b.tCap - a.tCap);
  }, [allBins, warehouses, selectedWarehouse, customerByWh, manualByWh, blockedByWh, incManual, incFloorBulk]);

  // Grand totals
  const T = useMemo(() => {
    const s = whSummaries;
    return {
      tCap: s.reduce((a, w) => a + w.tCap, 0), tUsed: s.reduce((a, w) => a + w.tUsed, 0),
      tFree: s.reduce((a, w) => a + w.tFree, 0),
      rSmall: s.reduce((a, w) => a + w.rackSmallBins, 0), rLarge: s.reduce((a, w) => a + w.rackLargeBins, 0),
      floor: s.reduce((a, w) => a + w.floorBulkBins, 0), flPal: s.reduce((a, w) => a + w.flPallets, 0),
      blocked: s.reduce((a, w) => a + w.tempBlockedBins, 0), blCap: s.reduce((a, w) => a + w.blCap, 0),
      virt: s.reduce((a, w) => a + w.virtualBins, 0), vUsed: s.reduce((a, w) => a + w.vUsed, 0),
      manual: s.reduce((a, w) => a + w.manPallets, 0), manCap: s.reduce((a, w) => a + w.manCap, 0),
      ibm: s.reduce((a, w) => a + w.ibmPallets, 0), l10: s.reduce((a, w) => a + w.level10Count, 0),
      exc: allBins.filter(b => b.excluded).length, ovr: allBins.filter(b => b.overridden).length,
      whCount: s.length,
    };
  }, [whSummaries, allBins]);

  const chartData = useMemo(() => whSummaries.map(w => ({ name: w.warehouse, Rack: w.rackCap, Used: w.rackUsed, Blocked: w.blCap, FloorBulk: w.flPallets, Manual: w.manPallets })), [whSummaries]);
  const pieData = useMemo(() => [
    { name: `Rack Small`, value: rackSmallBins.length, color: '#f59e0b' },
    { name: `Rack Large`, value: rackLargeBins.length, color: '#6366f1' },
    { name: 'Floor/Bulk', value: floorBulkBins.length, color: '#14b8a6' },
    { name: 'Blocked', value: tempBlockedArr.length, color: '#f43f5e' },
    { name: 'Virtual', value: filteredBins.filter(b => b.binType === 'VIRTUAL').length, color: '#06b6d4' },
  ].filter(d => d.value > 0), [rackSmallBins, rackLargeBins, floorBulkBins, tempBlockedArr, filteredBins]);

  // ============================================================
  // HANDLERS
  // ============================================================
  const toggleExclude = useCallback((id: string) => { setExcludedBins(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; }); }, []);

  const startEdit = (bin: BinRow) => { setEditingBin(bin.id); setEditType(bin.binType); setEditCap(bin.palletCapacity); };
  const applyOverride = () => {
    if (!editingBin) return;
    setBinOverrides(p => ({ ...p, [editingBin]: { newType: editType, customCap: editCap } }));
    setEditingBin(null);
  };
  const removeOverride = (id: string) => { setBinOverrides(p => { const n = { ...p }; delete n[id]; return n; }); };

  const sortedBins = useCallback((bins: BinRow[]) => {
    return [...bins].sort((a, b) => {
      let c = 0;
      switch (sortField) {
        case 'level': c = a.level - b.level; break;
        case 'capacity': c = a.palletCapacity - b.palletCapacity; break;
        case 'used': c = a.usedPallets - b.usedPallets; break;
        case 'status': c = a.status.localeCompare(b.status); break;
        default: c = a.warehouse.localeCompare(b.warehouse) || a.storageArea.localeCompare(b.storageArea); break;
      }
      return sortAsc ? c : -c;
    });
  }, [sortField, sortAsc]);
  const doSort = (f: typeof sortField) => { if (sortField === f) setSortAsc(!sortAsc); else { setSortField(f); setSortAsc(true); } setTablePage(0); };

  // Print report
  const printReport = () => {
    const el = reportRef.current;
    if (!el) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Capacity Report - ${fmtDate()}</title><style>body{font-family:Arial,sans-serif;padding:20px;color:#1f2937}table{width:100%;border-collapse:collapse;margin:10px 0}th,td{padding:6px 8px;text-align:left;border:1px solid #e5e7eb;font-size:11px}th{background:#f3f4f6;font-weight:600}.header{text-align:center;margin-bottom:20px}.stat-row{display:flex;gap:10px;margin:10px 0;flex-wrap:wrap}.stat-box{border:1px solid #e5e7eb;border-radius:6px;padding:10px;text-align:center;flex:1;min-width:100px}.big{font-size:18px;font-weight:700}.sub{font-size:9px;color:#6b7280}h3{margin:15px 0 5px;font-size:14px;border-bottom:2px solid #6366f1;padding-bottom:4px}.green{color:#10b981}.red{color:#f43f5e}.blue{color:#6366f1}.amber{color:#f59e0b}.teal{color:#14b8a6}.purple{color:#8b5cf6}@media print{body{padding:10px}}</style></head><body>`);
    w.document.write(el.innerHTML);
    w.document.write('</body></html>');
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ============================================================
  // STYLES
  // ============================================================
  const S = {
    card: { background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '16px', marginBottom: '12px' } as React.CSSProperties,
    stat: (c: string) => ({ background: `${c}12`, border: `1px solid ${c}25`, borderRadius: '10px', padding: '10px 12px', textAlign: 'center' as const, flex: 1, minWidth: '105px' }) as React.CSSProperties,
    btn: (on: boolean, c: string = colors.primary) => ({ padding: '5px 10px', borderRadius: '6px', border: on ? `2px solid ${c}` : `1px solid ${colors.border}`, background: on ? `${c}18` : colors.card, color: on ? c : colors.textSecondary, cursor: 'pointer', fontWeight: on ? 600 : 400, fontSize: '11px' }) as React.CSSProperties,
    th: { padding: '7px 7px', textAlign: 'left' as const, fontSize: '10px', fontWeight: 600, color: colors.textSecondary, borderBottom: `2px solid ${colors.border}`, cursor: 'pointer', userSelect: 'none' as const, whiteSpace: 'nowrap' as const } as React.CSSProperties,
    td: { padding: '5px 7px', fontSize: '11px', color: colors.text, borderBottom: `1px solid ${colors.borderLight}` } as React.CSSProperties,
    badge: (c: string) => ({ padding: '2px 6px', borderRadius: '10px', fontSize: '9px', fontWeight: 600, background: `${c}18`, color: c }) as React.CSSProperties,
    bar: { height: '5px', borderRadius: '3px', background: colors.borderLight, overflow: 'hidden' as const, width: '100%' } as React.CSSProperties,
    fill: (p: number) => ({ height: '100%', width: `${Math.min(p, 100)}%`, borderRadius: '3px', background: p > 90 ? '#f43f5e' : p > 70 ? '#f59e0b' : '#10b981', transition: 'width 0.3s' }) as React.CSSProperties,
    toggle: (on: boolean) => ({ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px 8px', borderRadius: '5px', fontSize: '10px', cursor: 'pointer', background: on ? '#10b98110' : '#f43f5e08', border: `1px solid ${on ? '#10b981' : '#f43f5e'}25`, color: on ? '#10b981' : '#f43f5e', fontWeight: 500 }) as React.CSSProperties,
  };
  const typeColor: Record<string, string> = { RACK_SMALL: '#f59e0b', RACK_LARGE: '#6366f1', FLOOR_BULK: '#14b8a6', TEMP_BLOCKED: '#f43f5e', VIRTUAL: '#06b6d4' };
  const typeLabel: Record<string, string> = { RACK_SMALL: 'SM', RACK_LARGE: 'LG', FLOOR_BULK: 'FLR', TEMP_BLOCKED: 'BLK', VIRTUAL: 'VIR' };

  // ============================================================
  // EXPORT section to CSV
  const exportSectionCSV = (bins: BinRow[], sectionName: string) => {
    const headers = ['Warehouse','Storage Area','Shelf Size','Level','Segments','Free Segments','Pallet Capacity','Used Pallets','Free Pallets','Util %','Status','Type','Level10','Overridden'];
    const rows = bins.map(b => [
      b.warehouse, b.storageArea, b.shelfSize, b.level, b.totalSegments, b.freeSegments,
      b.palletCapacity, b.usedPallets, b.freePallets,
      b.palletCapacity > 0 ? Math.round((b.usedPallets / b.palletCapacity) * 100) + '%' : '—',
      b.status, b.binType, b.isLevel10 ? 'Yes' : 'No', b.overridden ? 'Yes' : 'No'
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `dcv-${sectionName}-${fmtDate().replace(/[: ]/g, '-')}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // BIN TABLE with move/override actions
  // ============================================================
  const renderBinTable = (bins: BinRow[], title: string, accent: string) => {
    const sorted = sortedBins(bins);
    const ps = 100; const pg = tablePage;
    const paged = sorted.slice(pg * ps, (pg + 1) * ps);
    const tp = Math.ceil(sorted.length / ps);
    const tC = bins.reduce((s, b) => s + b.palletCapacity, 0);
    const tU = bins.reduce((s, b) => s + b.usedPallets, 0);
    const up = tC > 0 ? Math.round((tU / tC) * 100) : 0;
    const sectionKey = title.includes('Small') ? 'rack-small' : title.includes('Large') ? 'rack-large' : title.includes('Floor') ? 'floor-bulk' : 'blocked';
    return (
      <div style={S.card}>
        <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h3 style={{ margin: 0, color: colors.text, fontSize: '13px' }}>{title}</h3>
            <div style={{ display: 'flex', gap: '10px', marginTop: '2px', flexWrap: 'wrap', fontSize: '10px' }}>
              <span style={{ color: colors.textMuted }}>{bins.length} bins</span>
              <span style={{ fontWeight: 600, color: accent }}>Cap: {tC.toLocaleString()}</span>
              <span style={{ color: '#f43f5e' }}>Used: {tU.toLocaleString()}</span>
              <span style={{ color: '#10b981' }}>Free: {(tC - tU).toLocaleString()}</span>
              <span style={{ fontWeight: 700, color: up > 90 ? '#f43f5e' : up > 70 ? '#f59e0b' : '#10b981' }}>{up}%</span>
            </div>
          </div>
          <button onClick={() => exportSectionCSV(bins, sectionKey)} style={{ ...S.btn(false), padding: '4px 10px', fontSize: '9px', display: 'flex', alignItems: 'center', gap: '4px' }}>📥 Export CSV</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={S.th} onClick={() => doSort('warehouse')}>WH</th>
              <th style={S.th}>Area</th><th style={S.th}>Shelf</th>
              <th style={S.th} onClick={() => doSort('level')}>Lvl</th><th style={S.th}>Seg</th><th style={S.th}>Free Seg</th>
              <th style={S.th} onClick={() => doSort('capacity')}>P.Cap</th>
              <th style={S.th} onClick={() => doSort('used')}>Used</th><th style={S.th}>Free</th><th style={S.th}>Util%</th>
              <th style={S.th} onClick={() => doSort('status')}>Status</th><th style={S.th}>Type</th>
              {showAdvanced && <th style={S.th}>Actions</th>}
            </tr></thead>
            <tbody>{paged.map(b => (
              <tr key={b.id} style={{ opacity: b.excluded ? 0.3 : 1, background: b.overridden ? `${colors.primary}08` : b.binType === 'TEMP_BLOCKED' ? '#f43f5e05' : b.binType === 'FLOOR_BULK' ? '#14b8a606' : 'transparent' }}>
                <td style={S.td}><span style={{ fontWeight: 500 }}>{b.warehouse}</span></td>
                <td style={S.td}>{b.storageArea}</td>
                <td style={{ ...S.td, fontSize: '10px' }}>{b.shelfSize}</td>
                <td style={{ ...S.td, fontWeight: b.isLevel10 ? 700 : 400, color: b.isLevel10 ? '#f59e0b' : colors.text }}>{b.level}{b.isLevel10 ? '⭐' : ''}</td>
                <td style={S.td}>{b.totalSegments}</td>
                <td style={S.td}>{b.freeSegments}</td>
                <td style={{ ...S.td, fontWeight: 600, color: b.overridden ? colors.primary : accent }}>{b.palletCapacity}{b.overridden ? ' *' : ''}</td>
                <td style={{ ...S.td, color: b.usedPallets > 0 ? '#f43f5e' : '#10b981' }}>{b.usedPallets}</td>
                <td style={{ ...S.td, color: '#10b981' }}>{b.freePallets}</td>
                <td style={{ ...S.td, fontWeight: 600, color: b.palletCapacity > 0 ? (Math.round((b.usedPallets / b.palletCapacity) * 100) > 90 ? '#f43f5e' : Math.round((b.usedPallets / b.palletCapacity) * 100) > 70 ? '#f59e0b' : '#10b981') : colors.textMuted }}>{b.palletCapacity > 0 ? `${Math.round((b.usedPallets / b.palletCapacity) * 100)}%` : '—'}</td>
                <td style={S.td}><span style={S.badge(b.status === 'FULL' || b.status === 'TEMP_BLOCKED' ? '#f43f5e' : b.status === 'FLOOR/BULK' ? '#14b8a6' : b.status === 'EMPTY' ? '#10b981' : '#f59e0b')}>{b.status}</span></td>
                <td style={S.td}>
                  <span style={S.badge(typeColor[b.binType])}>{typeLabel[b.binType]}</span>
                  {b.overridden && <span style={{ ...S.badge('#ec4899'), marginLeft: '2px' }}>OVR</span>}
                </td>
                {showAdvanced && <td style={S.td}>
                  <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                    <button onClick={() => toggleExclude(b.id)} style={{ padding: '1px 5px', borderRadius: '3px', fontSize: '8px', cursor: 'pointer', border: `1px solid ${b.excluded ? '#10b981' : '#f43f5e'}20`, background: b.excluded ? '#10b98108' : '#f43f5e08', color: b.excluded ? '#10b981' : '#f43f5e' }}>{b.excluded ? '+Keep' : '−Rem'}</button>
                    <button onClick={() => startEdit(b)} style={{ padding: '1px 5px', borderRadius: '3px', fontSize: '8px', cursor: 'pointer', border: `1px solid ${colors.primary}20`, background: `${colors.primary}08`, color: colors.primary }}>Move</button>
                    {b.overridden && <button onClick={() => removeOverride(b.id)} style={{ padding: '1px 5px', borderRadius: '3px', fontSize: '8px', cursor: 'pointer', border: '1px solid #f59e0b20', background: '#f59e0b08', color: '#f59e0b' }}>Reset</button>}
                  </div>
                </td>}
              </tr>
            ))}</tbody>
          </table>
        </div>
        {tp > 1 && <div className="dcv-pagination">
          <button onClick={() => setTablePage(Math.max(0, pg - 1))} disabled={pg === 0} style={{ ...S.btn(false), opacity: pg === 0 ? 0.3 : 1 }}>Prev</button>
          <span style={{ fontSize: '9px', color: colors.textSecondary, lineHeight: '28px' }}>Pg {pg + 1}/{tp}</span>
          <button onClick={() => setTablePage(Math.min(tp - 1, pg + 1))} disabled={pg >= tp - 1} style={{ ...S.btn(false), opacity: pg >= tp - 1 ? 0.3 : 1 }}>Next</button>
        </div>}
      </div>
    );
  };

  // ============================================================
  // EMPTY STATE
  // ============================================================
  if (!rawWarehouseData || rawWarehouseData.length === 0) {
    return <div style={{ ...S.card, textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔬</div>
      <h2 style={{ color: colors.text }}>Dynamic Capacity Verification</h2>
      <p style={{ color: colors.textMuted }}>No warehouse data loaded yet.</p>
    </div>;
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="dcv-container">
      {/* EDIT OVERLAY */}
      {editingBin && (
        <div className="dcv-overlay">
          <div className="dcv-overlay-card">
            <h3 className="dcv-overlay-title">Move / Reclassify Bin</h3>
            <p style={{ fontSize: '11px', color: colors.textMuted, marginBottom: '12px' }}>Bin: {editingBin}</p>
            <div style={{ marginBottom: '10px' }}>
              <label className="dcv-overlay-label">New Type:</label>
              <select value={editType} onChange={e => setEditType(e.target.value as BinType)} className="dcv-overlay-select">
                <option value="RACK_SMALL">Rack Small (≤{threshold} seg rule)</option>
                <option value="RACK_LARGE">Rack Large (segment count)</option>
                <option value="FLOOR_BULK">Floor/Bulk</option>
              </select>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label className="dcv-overlay-label">Custom Pallet Capacity:</label>
              <input type="number" value={editCap} onChange={e => setEditCap(Math.max(0, parseInt(e.target.value) || 0))} className="dcv-overlay-input" min={0} />
              <span style={{ fontSize: '9px', color: colors.textMuted }}>Set to 0 to use auto-calculation</span>
            </div>
            <div className="dcv-overlay-actions">
              <button onClick={() => setEditingBin(null)} style={{ ...S.btn(false), padding: '6px 14px' }}>Cancel</button>
              <button onClick={applyOverride} style={{ ...S.btn(true, '#10b981'), padding: '6px 14px' }}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="dcv-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h2 className="dcv-title">🔬 Dynamic Capacity Verification</h2>
            <p className="dcv-subtitle">
              Rack + Floor/Bulk + Virtual + Manual + Blocked | Threshold ≤{threshold} |
              {T.ovr > 0 && <span style={{ color: '#ec4899' }}> {T.ovr} overrides |</span>}
              {T.exc > 0 && <span style={{ color: '#f43f5e' }}> {T.exc} excluded</span>}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: `${colors.primary}10`, padding: '4px 8px', borderRadius: '6px', border: `1px solid ${colors.primary}20` }}>
              <span style={{ fontSize: '9px', color: colors.textSecondary }}>Thr:</span>
              <input type="number" value={threshold} onChange={e => { setThreshold(Math.max(1, parseInt(e.target.value) || 4)); setTablePage(0); }}
                style={{ width: '38px', padding: '2px 4px', borderRadius: '4px', border: `1px solid ${colors.border}`, background: colors.backgroundSecondary, color: colors.text, fontWeight: 600, fontSize: '11px', textAlign: 'center' as const }} min={1} max={20} />
            </div>
            {[2, 3, 4, 5, 6, 8].map(t => <button key={t} onClick={() => { setThreshold(t); setTablePage(0); }} style={{ ...S.btn(threshold === t), padding: '2px 6px', fontSize: '9px' }}>≤{t}</button>)}
          </div>
        </div>

        {/* NAV */}
        <div className="dcv-nav">
          <select value={selectedWarehouse} onChange={e => { setSelectedWarehouse(e.target.value); setTablePage(0); setExpandedWh(null); }}
            style={{ padding: '5px 7px', borderRadius: '5px', border: `1px solid ${colors.border}`, background: colors.backgroundSecondary, color: colors.text, fontSize: '10px' }}>
            <option value="ALL">All WH ({warehouses.length})</option>
            {warehouses.map(w => <option key={w} value={w}>{w} {WH_NAMES[w] ? `- ${WH_NAMES[w]}` : ''}</option>)}
          </select>
          <input type="text" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setTablePage(0); }} placeholder="Search..."
            style={{ padding: '5px 7px', borderRadius: '5px', border: `1px solid ${colors.border}`, background: colors.backgroundSecondary, color: colors.text, fontSize: '10px', width: '110px' }} />
          {([
            ['overview', '📊 Overview', colors.primary], ['rackSmall', '📦 Rack SM', '#f59e0b'], ['rackLarge', '📐 Rack LG', '#6366f1'],
            ['floorBulk', '🏗️ Floor', '#14b8a6'], ['tempBlocked', '🚫 Blocked', '#f43f5e'], ['manual', '📝 Manual', '#8b5cf6'],
            ['customers', '👥 Customers', '#ec4899'], ['compare', '⚖️ Compare', '#06b6d4'], ['report', '📄 Report', '#10b981'],
          ] as [string, string, string][]).map(([m, l, c]) =>
            <button key={m} className={`dcv-nav-btn ${viewMode === m ? 'active' : ''}`} style={{ color: viewMode === m ? c : colors.textSecondary }} onClick={() => { setViewMode(m as any); setTablePage(0); }}>{l}</button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '3px', alignItems: 'center' }}>
            <button style={S.btn(showCharts, '#06b6d4')} onClick={() => setShowCharts(!showCharts)}>📈</button>
            <button style={S.btn(showAdvanced, '#f43f5e')} onClick={() => setShowAdvanced(!showAdvanced)}>⚙️</button>
            {/* SAVE/LOAD */}
            <button onClick={saveChanges} style={{ ...S.btn(false, '#10b981'), background: hasUnsaved ? '#10b98120' : colors.card, color: hasUnsaved ? '#10b981' : colors.textSecondary, fontWeight: hasUnsaved ? 600 : 400 }}>
              💾 Save{hasUnsaved ? '*' : ''}
            </button>
          </div>
        </div>

        {/* ADVANCED */}
        {showAdvanced && (
          <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center', paddingTop: '8px', borderTop: `1px solid ${colors.borderLight}` }}>
            <span style={{ fontSize: '9px', color: colors.textSecondary, fontWeight: 500 }}>Sources:</span>
            <button style={S.toggle(incVirtual)} onClick={() => setIncVirtual(!incVirtual)}>{incVirtual ? '✓' : '✗'} Virtual</button>
            <button style={S.toggle(incManual)} onClick={() => setIncManual(!incManual)}>{incManual ? '✓' : '✗'} Manual</button>
            <button style={S.toggle(incFloorBulk)} onClick={() => setIncFloorBulk(!incFloorBulk)}>{incFloorBulk ? '✓' : '✗'} Floor/Bulk</button>
            {T.exc > 0 && <button onClick={() => setExcludedBins(new Set())} style={{ ...S.btn(false, '#10b981'), fontSize: '9px', padding: '2px 6px' }}>Restore {T.exc} excluded</button>}
            {T.ovr > 0 && <button onClick={() => setBinOverrides({})} style={{ ...S.btn(false, '#f59e0b'), fontSize: '9px', padding: '2px 6px' }}>Clear {T.ovr} overrides</button>}
            <button onClick={exportChanges} style={{ ...S.btn(false, '#06b6d4'), fontSize: '9px', padding: '2px 6px' }}>📥 Export JSON</button>
            <button onClick={clearSaved} style={{ ...S.btn(false, '#f43f5e'), fontSize: '9px', padding: '2px 6px' }}>🗑️ Clear saved</button>
            {saveStatus && <span style={{ fontSize: '9px', color: '#10b981', fontWeight: 500 }}>{saveStatus}</span>}
            {saved?.savedAt && <span style={{ fontSize: '8px', color: colors.textMuted }}>Last saved: {saved.savedAt}</span>}
          </div>
        )}
      </div>

      {/* TOTALS */}
      <div className="dcv-stat-grid">
        {[
          [colors.primary, T.tCap, 'Total Cap'], ['#f43f5e', T.tUsed, 'Total Used'], ['#10b981', T.tFree, 'Total Free'],
          ['#f59e0b', T.rSmall, 'Rack SM'], ['#6366f1', T.rLarge, 'Rack LG'],
          ['#14b8a6', T.flPal, 'Floor/Bulk Pal'], ['#f43f5e', T.blocked, 'Blocked'],
          ['#8b5cf6', T.manual, 'Manual'], ['#06b6d4', T.virt, 'Virtual'], ['#f59e0b', T.l10, 'Level 10'],
        ].map(([c, v, l], i) => (
          <div key={i} className="dcv-stat-chip">
            <div className="dcv-stat-value" style={{ color: c as string }}>{(v as number).toLocaleString()}</div>
            <div className="dcv-stat-label">{l as string}</div>
          </div>
        ))}
      </div>

      {/* SM vs LG vs FLOOR */}
      <div className="dcv-bin-cards">
        {[
          { l: '📦 Rack Small', c: '#f59e0b', b: rackSmallBins, n: `≤${threshold} seg | Lvl10=2p`, isFloor: false },
          { l: '📐 Rack Large', c: '#6366f1', b: rackLargeBins, n: `>${threshold} seg | Cap=seg`, isFloor: false },
          { l: '🏗️ Floor/Bulk', c: '#14b8a6', b: floorBulkBins, n: '0 seg | IBM pallets', isFloor: true },
        ].map(({ l, c, b, n, isFloor: isFloorCard }, idx) => {
          const cap = isFloorCard ? 0 : b.reduce((s, x) => s + x.palletCapacity, 0);
          const u = isFloorCard ? T.flPal : b.reduce((s, x) => s + x.usedPallets, 0);
          const p = cap > 0 ? Math.round((u / cap) * 100) : (isFloorCard && u > 0 ? 100 : 0);
          const binType = idx === 0 ? 'sm' : idx === 1 ? 'lg' : 'floor';
          return <div key={l} className={`dcv-bin-card ${binType}`} style={{ borderLeft: `3px solid ${c}` }}>
            <h3 className="dcv-bin-card-title" style={{ color: c }}>{l}</h3>
            <div className="dcv-bin-card-sub">{n}</div>
            <div style={{ display: 'flex', gap: '10px', fontSize: '14px', fontWeight: 700 }}>
              {isFloorCard ? (
                <>
                  <span style={{ color: '#f43f5e' }}>{u.toLocaleString()} <span style={{ fontSize: '8px', fontWeight: 400, color: colors.textMuted }}>pallets (IBM derived)</span></span>
                </>
              ) : (
                <>
                  <span>{cap.toLocaleString()} <span style={{ fontSize: '8px', fontWeight: 400, color: colors.textMuted }}>cap</span></span>
                  <span style={{ color: '#f43f5e' }}>{u.toLocaleString()} <span style={{ fontSize: '8px', fontWeight: 400, color: colors.textMuted }}>used</span></span>
                  <span style={{ color: '#10b981' }}>{(cap - u).toLocaleString()} <span style={{ fontSize: '8px', fontWeight: 400, color: colors.textMuted }}>free</span></span>
                </>
              )}
            </div>
            <div className="dcv-bin-stats">
              {!isFloorCard && <div className="dcv-progress"><div className="dcv-progress-fill" style={S.fill(p)} /></div>}
              <div style={{ fontSize: '8px', color: colors.textMuted, marginTop: '1px' }}>
                {isFloorCard ? `${b.length} bins (0 seg) | Pallets = IBM total - Rack used - Blocked` : `${p}% | ${b.length} bins`}
              </div>
            </div>
          </div>;
        })}
      </div>

      {/* CHARTS */}
      {showCharts && viewMode !== 'report' && (
        <div className="dcv-charts">
          <div className="dcv-card">
            <h3 style={{ margin: '0 0 6px', fontSize: '12px', color: colors.text }}>Warehouse Breakdown</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.borderLight} />
                <XAxis dataKey="name" tick={{ fontSize: 8, fill: colors.textMuted }} />
                <YAxis tick={{ fontSize: 8, fill: colors.textMuted }} />
                <Tooltip contentStyle={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '5px', fontSize: '9px' }} />
                <Bar dataKey="Rack" fill="#6366f1" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Used" fill="#f43f5e" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Blocked" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                <Bar dataKey="FloorBulk" fill="#14b8a6" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="dcv-card">
            <h3 style={{ margin: '0 0 6px', fontSize: '12px', color: colors.text }}>Bin Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={70} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={{ stroke: colors.textMuted }}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '5px', fontSize: '9px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ===== VIEW: OVERVIEW ===== */}
      {viewMode === 'overview' && (
        <div className="dcv-card">
          <h3 className="dcv-card-title">📊 Full Warehouse Summary</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={S.th}>WH</th><th style={{ ...S.th, color: '#f59e0b' }}>SM</th><th style={{ ...S.th, color: '#6366f1' }}>LG</th>
                <th style={{ ...S.th, color: '#14b8a6' }}>Floor</th><th style={{ ...S.th, color: '#f43f5e' }}>Blk</th>
                <th style={{ ...S.th, color: '#06b6d4' }}>Vir</th><th style={{ ...S.th, color: '#f59e0b' }}>L10</th>
                <th style={{ ...S.th, color: '#ec4899' }}>IBM</th><th style={{ ...S.th, color: '#8b5cf6' }}>Manual</th>
                <th style={{ ...S.th, fontWeight: 700 }}>Cap</th><th style={{ ...S.th, fontWeight: 700 }}>Used</th><th style={S.th}>%</th>
                <th style={S.th}></th>
              </tr></thead>
              <tbody>
                {whSummaries.map(w => (
                  <React.Fragment key={w.warehouse}>
                    <tr style={{ background: expandedWh === w.warehouse ? `${colors.primary}05` : 'transparent' }}>
                      <td style={S.td}><span style={{ fontWeight: 600, fontSize: '10px' }}>{w.warehouse}</span>{w.overrideCount > 0 && <span style={{ ...S.badge('#ec4899'), marginLeft: '3px' }}>{w.overrideCount} ovr</span>}<span style={{ fontSize: '7px', color: colors.textMuted, display: 'block' }}>{w.displayName}</span></td>
                      <td style={{ ...S.td, color: '#f59e0b' }}>{w.rackSmallBins}</td>
                      <td style={{ ...S.td, color: '#6366f1' }}>{w.rackLargeBins}</td>
                      <td style={{ ...S.td, color: '#14b8a6' }}>{w.floorBulkBins > 0 ? `${w.floorBulkBins}/${w.flPallets}p` : '—'}</td>
                      <td style={{ ...S.td, color: '#f43f5e' }}>{w.tempBlockedBins > 0 ? `${w.tempBlockedBins}/${w.blCap}p` : '—'}</td>
                      <td style={{ ...S.td, color: '#06b6d4' }}>{w.virtualBins > 0 ? `${w.virtualBins}/${w.vUsed}p` : '—'}</td>
                      <td style={{ ...S.td, color: '#f59e0b', fontWeight: 600 }}>{w.level10Count}</td>
                      <td style={{ ...S.td, color: '#ec4899' }}>{w.ibmPallets > 0 ? `${w.ibmPallets}(${w.ibmCustomers}c)` : '—'}</td>
                      <td style={{ ...S.td, color: '#8b5cf6' }}>{w.manPallets > 0 ? `${w.manPallets}p/${w.manCap}c` : '—'}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: colors.primary }}>{w.tCap.toLocaleString()}</td>
                      <td style={{ ...S.td, fontWeight: 600, color: '#f43f5e' }}>{w.tUsed.toLocaleString()}</td>
                      <td style={S.td}><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ ...S.bar, width: '40px' }}><div style={S.fill(w.utilPct)} /></div><span style={{ fontSize: '9px', fontWeight: 600, color: w.utilPct > 90 ? '#f43f5e' : w.utilPct > 70 ? '#f59e0b' : '#10b981' }}>{w.utilPct}%</span></div></td>
                      <td style={S.td}><button onClick={() => setExpandedWh(expandedWh === w.warehouse ? null : w.warehouse)} style={{ padding: '1px 5px', borderRadius: '3px', border: `1px solid ${colors.primary}20`, background: `${colors.primary}08`, color: colors.primary, cursor: 'pointer', fontSize: '8px' }}>{expandedWh === w.warehouse ? '▲' : '▼'}</button></td>
                    </tr>
                    {expandedWh === w.warehouse && <tr><td colSpan={13} style={{ padding: '0 5px 6px' }}>
                      <div style={{ background: colors.backgroundSecondary, borderRadius: '6px', padding: '6px', maxHeight: '300px', overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}><thead><tr>
                          <th style={{ ...S.th, fontSize: '8px' }}>Area</th><th style={{ ...S.th, fontSize: '8px' }}>Shelf</th><th style={{ ...S.th, fontSize: '8px' }}>Lvl</th>
                          <th style={{ ...S.th, fontSize: '8px' }}>Seg</th><th style={{ ...S.th, fontSize: '8px' }}>Type</th><th style={{ ...S.th, fontSize: '8px' }}>Cap</th>
                          <th style={{ ...S.th, fontSize: '8px' }}>Used</th><th style={{ ...S.th, fontSize: '8px' }}>Status</th>
                          {showAdvanced && <th style={{ ...S.th, fontSize: '8px' }}>Act</th>}
                        </tr></thead><tbody>
                          {allBins.filter(b => b.warehouse === w.warehouse).sort((a, b) => a.storageArea.localeCompare(b.storageArea) || a.level - b.level).slice(0, 250).map(b => (
                            <tr key={b.id} style={{ opacity: b.excluded ? 0.3 : 1, background: b.overridden ? `${colors.primary}06` : 'transparent' }}>
                              <td style={{ ...S.td, fontSize: '9px' }}>{b.storageArea}</td>
                              <td style={{ ...S.td, fontSize: '9px' }}>{b.shelfSize}</td>
                              <td style={{ ...S.td, fontSize: '9px', fontWeight: b.isLevel10 ? 700 : 400, color: b.isLevel10 ? '#f59e0b' : colors.text }}>{b.level}{b.isLevel10 ? '⭐' : ''}</td>
                              <td style={{ ...S.td, fontSize: '9px' }}>{b.totalSegments}</td>
                              <td style={{ ...S.td, fontSize: '9px' }}><span style={S.badge(typeColor[b.binType])}>{typeLabel[b.binType]}</span>{b.overridden && <span style={S.badge('#ec4899')}>*</span>}</td>
                              <td style={{ ...S.td, fontSize: '9px', fontWeight: 600, color: colors.primary }}>{b.palletCapacity}</td>
                              <td style={{ ...S.td, fontSize: '9px', color: b.usedPallets > 0 ? '#f43f5e' : '#10b981' }}>{b.usedPallets}</td>
                              <td style={{ ...S.td, fontSize: '9px' }}><span style={S.badge(b.status === 'FULL' || b.status === 'TEMP_BLOCKED' ? '#f43f5e' : b.status === 'EMPTY' || b.status === 'FLOOR/BULK' ? '#14b8a6' : '#f59e0b')}>{b.status}</span></td>
                              {showAdvanced && <td style={{ ...S.td, fontSize: '9px' }}>
                                <button onClick={() => startEdit(b)} style={{ padding: '0 4px', borderRadius: '2px', fontSize: '7px', cursor: 'pointer', border: `1px solid ${colors.primary}20`, background: `${colors.primary}06`, color: colors.primary }}>Move</button>
                              </td>}
                            </tr>
                          ))}
                        </tbody></table>
                      </div>
                    </td></tr>}
                  </React.Fragment>
                ))}
                <tr style={{ background: `${colors.primary}05`, fontWeight: 600 }}>
                  <td style={S.td}>TOTAL ({T.whCount})</td>
                  <td style={{ ...S.td, color: '#f59e0b' }}>{T.rSmall}</td><td style={{ ...S.td, color: '#6366f1' }}>{T.rLarge}</td>
                  <td style={{ ...S.td, color: '#14b8a6' }}>{T.floor}/{T.flPal}p</td><td style={{ ...S.td, color: '#f43f5e' }}>{T.blocked}/{T.blCap}p</td>
                  <td style={{ ...S.td, color: '#06b6d4' }}>{T.virt}/{T.vUsed}p</td><td style={{ ...S.td, color: '#f59e0b' }}>{T.l10}</td>
                  <td style={{ ...S.td, color: '#ec4899' }}>{T.ibm}</td><td style={{ ...S.td, color: '#8b5cf6' }}>{T.manual}p/{T.manCap}c</td>
                  <td style={{ ...S.td, color: colors.primary }}>{T.tCap.toLocaleString()}</td><td style={{ ...S.td, color: '#f43f5e' }}>{T.tUsed.toLocaleString()}</td>
                  <td style={S.td}>{(() => { const p = T.tCap > 0 ? Math.round((T.tUsed / T.tCap) * 100) : 0; return <span style={{ fontWeight: 700, color: p > 90 ? '#f43f5e' : p > 70 ? '#f59e0b' : '#10b981' }}>{p}%</span>; })()}</td>
                  <td style={S.td}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewMode === 'rackSmall' && renderBinTable(rackSmallBins, `📦 Rack Small (≤${threshold} seg) — Lvl10=2p, other=1p`, '#f59e0b')}
      {viewMode === 'rackLarge' && renderBinTable(rackLargeBins, `📐 Rack Large (>${threshold} seg) — Cap = segments`, '#6366f1')}
      {viewMode === 'floorBulk' && renderBinTable(floorBulkBins, '🏗️ Floor/Bulk (0 seg) — Non-racked', '#14b8a6')}
      {viewMode === 'tempBlocked' && renderBinTable(tempBlockedArr, '🚫 Temp Blocked — 100% used', '#f43f5e')}

      {viewMode === 'manual' && (
        <div className="dcv-card">
          <h3 className="dcv-card-title" style={{ color: '#8b5cf6' }}>📝 Manual Entries</h3>
          {Object.keys(manualByWh).length === 0 ? <p style={{ color: colors.textMuted, fontSize: '10px' }}>No manual entries.</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}><thead><tr>
              <th style={S.th}>WH</th><th style={S.th}>Clients</th><th style={S.th}>Pallets</th><th style={S.th}>Capacity</th><th style={S.th}>Util</th>
            </tr></thead><tbody>
              {Object.entries(manualByWh).filter(([w]) => whFilter(w)).sort(([, a], [, b]) => b.pallets - a.pallets).map(([w, d]) => {
                const u = d.capacity > 0 ? Math.round((d.pallets / d.capacity) * 100) : 0;
                return <tr key={w}><td style={S.td}><span style={{ fontWeight: 600 }}>{w}</span></td><td style={S.td}>{d.clients.length}</td>
                  <td style={{ ...S.td, fontWeight: 600, color: '#8b5cf6' }}>{d.pallets.toLocaleString()}</td>
                  <td style={{ ...S.td, color: colors.primary }}>{d.capacity.toLocaleString()}</td>
                  <td style={S.td}><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ ...S.bar, width: '40px' }}><div style={S.fill(u)} /></div><span style={{ fontSize: '9px', fontWeight: 600 }}>{u}%</span></div></td>
                </tr>;
              })}
            </tbody></table>
          )}
        </div>
      )}

      {viewMode === 'customers' && (
        <div style={S.card}>
          <h3 style={{ margin: '0 0 8px', color: '#ec4899', fontSize: '13px' }}>👥 Customer Storage (IBM)</h3>
          {(!customerStorageData || !customerStorageData.length) ? <p style={{ color: colors.textMuted, fontSize: '10px' }}>No data.</p> : (
            <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={S.th}>WH</th><th style={S.th}>Customer</th><th style={S.th}>Location</th><th style={S.th}>Type</th><th style={S.th}>Pallets</th></tr></thead>
              <tbody>{customerStorageData.filter(c => whFilter(c.WAREHOUSE)).sort((a, b) => (b.USED_PALLETS || 0) - (a.USED_PALLETS || 0)).slice(0, 200).map((c, i) => (
                <tr key={i}><td style={S.td}>{c.WAREHOUSE}</td><td style={S.td}>{c.CUSTOMER}</td><td style={S.td}>{c.STORAGE_LOCATION}</td>
                  <td style={S.td}><span style={S.badge(c.STORAGE_TYPE === 'MANUAL' ? '#8b5cf6' : '#6366f1')}>{c.STORAGE_TYPE}</span></td>
                  <td style={{ ...S.td, fontWeight: 600, color: '#ec4899' }}>{(c.USED_PALLETS || 0).toLocaleString()}</td></tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      )}

      {viewMode === 'compare' && (
        <div style={S.card}>
          <h3 style={{ margin: '0 0 8px', color: '#06b6d4', fontSize: '13px' }}>⚖️ Raw Segments vs Pallet Capacity</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={S.th}>WH</th><th style={S.th}>Raw Seg</th><th style={S.th}>Pallet Cap</th><th style={S.th}>Diff</th><th style={S.th}>%</th><th style={S.th}>Raw Used</th><th style={S.th}>Pal Used</th><th style={S.th}>Diff</th></tr></thead>
            <tbody>{warehouses.filter(whFilter).map(wh => {
              const wb = allBins.filter(b => b.warehouse === wh && !b.excluded); if (!wb.length) return null;
              const rs = wb.reduce((s, b) => s + b.totalSegments, 0); const pc = wb.reduce((s, b) => s + b.palletCapacity, 0);
              const ru = wb.reduce((s, b) => s + (b.totalSegments - b.freeSegments), 0); const pu = wb.reduce((s, b) => s + b.usedPallets, 0);
              const d = pc - rs; const pct = rs > 0 ? Math.round((d / rs) * 100) : 0;
              return <tr key={wh}>
                <td style={S.td}><span style={{ fontWeight: 600 }}>{wh}</span></td>
                <td style={{ ...S.td, color: colors.textMuted }}>{rs.toLocaleString()}</td>
                <td style={{ ...S.td, fontWeight: 600, color: colors.primary }}>{pc.toLocaleString()}</td>
                <td style={{ ...S.td, fontWeight: 600, color: d < 0 ? '#f43f5e' : d > 0 ? '#10b981' : colors.textMuted }}>{d > 0 ? '+' : ''}{d.toLocaleString()}</td>
                <td style={{ ...S.td, color: pct < 0 ? '#f43f5e' : pct > 0 ? '#10b981' : colors.textMuted }}>{pct > 0 ? '+' : ''}{pct}%</td>
                <td style={{ ...S.td, color: colors.textMuted }}>{ru.toLocaleString()}</td>
                <td style={{ ...S.td, fontWeight: 600, color: '#f43f5e' }}>{pu.toLocaleString()}</td>
                <td style={{ ...S.td, color: (pu - ru) !== 0 ? ((pu - ru) > 0 ? '#f43f5e' : '#10b981') : colors.textMuted }}>{(pu - ru) > 0 ? '+' : ''}{(pu - ru).toLocaleString()}</td>
              </tr>;
            }).filter(Boolean)}</tbody>
          </table>
        </div>
      )}

      {/* ===== AUTO-REPORT ===== */}
      {viewMode === 'report' && (
        <div className="dcv-report">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px', gap: '6px', flexWrap: 'wrap' }}>
            <button onClick={() => {
              const headers = ['Warehouse','Name','Total Bins','Level 10','Rack SM Bins','Rack LG Bins','Floor Bins','Blocked Bins','Virtual Bins','Rack Cap','Rack Used','Rack %','Floor Pallets','Blocked Cap','Virtual Used','Manual Used','Manual Cap','Total Cap','Total Used','Total Free','Util %'];
              const rows = whSummaries.map(w => [w.warehouse, w.displayName, w.rackSmallBins+w.rackLargeBins+w.floorBulkBins+w.tempBlockedBins, w.level10Count, w.rackSmallBins, w.rackLargeBins, w.floorBulkBins, w.tempBlockedBins, w.virtualBins, w.rackCap, w.rackUsed, w.rackCap > 0 ? Math.round((w.rackUsed/w.rackCap)*100)+'%' : '0%', w.flPallets, w.blCap, w.vUsed, w.manPallets, w.manCap, w.tCap, w.tUsed, w.tFree, w.utilPct+'%']);
              const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = `dcv-report-${fmtDate().replace(/[: ]/g, '-')}.csv`; a.click();
              URL.revokeObjectURL(url);
            }} style={{ ...S.btn(true, '#6366f1'), padding: '6px 14px' }}>📊 Export Report CSV</button>
            <button onClick={printReport} style={{ ...S.btn(true, '#10b981'), padding: '6px 14px' }}>🖨️ Print Report</button>
            <button onClick={exportChanges} style={{ ...S.btn(false, '#06b6d4'), padding: '6px 14px' }}>📥 Export Config</button>
          </div>
          <div ref={reportRef} style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '24px' }}>
            <div className="dcv-report-header">
              <h2 className="dcv-report-title">Warehouse Capacity Verification Report</h2>
              <p className="dcv-report-subtitle">Generated: {fmtDate()} | Threshold: ≤{threshold} segments | Filter: {selectedWarehouse === 'ALL' ? 'All Warehouses' : `${selectedWarehouse} (${WH_NAMES[selectedWarehouse] || ''})`}</p>
              {(T.ovr > 0 || T.exc > 0) && <p style={{ margin: '4px 0 0', fontSize: '10px', color: '#ec4899' }}>User modifications: {T.ovr} bin overrides, {T.exc} bins excluded</p>}
            </div>

            {/* Report Summary */}
            <h3 className="dcv-report-section-title">Executive Summary</h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
              {[
                [colors.primary, T.tCap, 'Total Pallet Capacity'], ['#f43f5e', T.tUsed, 'Total Used Pallets'],
                ['#10b981', T.tFree, 'Total Free Pallets'],
                [colors.primary, T.tCap > 0 ? `${Math.round((T.tUsed / T.tCap) * 100)}%` : '0%', 'Overall Utilization'],
              ].map(([c, v, l], i) => (
                <div key={i} style={{ border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '12px', textAlign: 'center', flex: 1, minWidth: '130px' }}>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: c as string }}>{typeof v === 'number' ? v.toLocaleString() : v}</div>
                  <div style={{ fontSize: '10px', color: colors.textMuted }}>{l as string}</div>
                </div>
              ))}
            </div>

            {/* Breakdown */}
            <h3 className="dcv-report-section-title">Capacity Breakdown by Source</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
              <thead><tr>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Source</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Bins</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Capacity</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Used</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Free</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Util %</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Notes</th>
              </tr></thead>
              <tbody>
                {(() => {
                  const rsCap = rackSmallBins.reduce((s, b) => s + b.palletCapacity, 0);
                  const rsUsed = rackSmallBins.reduce((s, b) => s + b.usedPallets, 0);
                  const rsFree = rackSmallBins.reduce((s, b) => s + b.freePallets, 0);
                  const rsPct = rsCap > 0 ? Math.round((rsUsed / rsCap) * 100) : 0;
                  const rlCap = rackLargeBins.reduce((s, b) => s + b.palletCapacity, 0);
                  const rlUsed = rackLargeBins.reduce((s, b) => s + b.usedPallets, 0);
                  const rlFree = rackLargeBins.reduce((s, b) => s + b.freePallets, 0);
                  const rlPct = rlCap > 0 ? Math.round((rlUsed / rlCap) * 100) : 0;
                  const manFree = Math.max(0, T.manCap - T.manual);
                  const manPct = T.manCap > 0 ? Math.round((T.manual / T.manCap) * 100) : 0;
                  const tPct = T.tCap > 0 ? Math.round((T.tUsed / T.tCap) * 100) : 0;
                  const pctStyle = (pct: number) => ({ fontWeight: 700, color: pct > 90 ? '#f43f5e' : pct > 70 ? '#f59e0b' : '#10b981' });
                  return <>
                    <tr><td style={S.td}>📦 Rack Small (≤{threshold} seg)</td><td style={S.td}>{T.rSmall.toLocaleString()}</td><td style={S.td}>{rsCap.toLocaleString()}</td><td style={S.td}>{rsUsed.toLocaleString()}</td><td style={S.td}>{rsFree.toLocaleString()}</td><td style={{ ...S.td, ...pctStyle(rsPct) }}>{rsPct}%</td><td style={{ ...S.td, fontSize: '9px' }}>Level 10 = 2 pallets, other = 1</td></tr>
                    <tr><td style={S.td}>📐 Rack Large (&gt;{threshold} seg)</td><td style={S.td}>{T.rLarge.toLocaleString()}</td><td style={S.td}>{rlCap.toLocaleString()}</td><td style={S.td}>{rlUsed.toLocaleString()}</td><td style={S.td}>{rlFree.toLocaleString()}</td><td style={{ ...S.td, ...pctStyle(rlPct) }}>{rlPct}%</td><td style={{ ...S.td, fontSize: '9px' }}>Capacity = actual segments</td></tr>
                    <tr><td style={S.td}>🏗️ Floor/Bulk</td><td style={S.td}>{T.floor.toLocaleString()}</td><td style={S.td}>—</td><td style={S.td}>{T.flPal.toLocaleString()}</td><td style={S.td}>—</td><td style={S.td}>—</td><td style={{ ...S.td, fontSize: '9px' }}>Non-racked | IBM total − Rack used − Blocked</td></tr>
                    <tr><td style={S.td}>🚫 Temp Blocked</td><td style={S.td}>{T.blocked.toLocaleString()}</td><td style={S.td}>{T.blCap.toLocaleString()}</td><td style={S.td}>{T.blCap.toLocaleString()}</td><td style={S.td}>0</td><td style={{ ...S.td, ...pctStyle(100) }}>100%</td><td style={{ ...S.td, fontSize: '9px' }}>100% capacity blocked</td></tr>
                    <tr><td style={S.td}>📡 Virtual</td><td style={S.td}>{T.virt.toLocaleString()}</td><td style={S.td}>—</td><td style={S.td}>{T.vUsed.toLocaleString()}</td><td style={S.td}>—</td><td style={S.td}>—</td><td style={{ ...S.td, fontSize: '9px' }}>Virtual bins used</td></tr>
                    <tr><td style={S.td}>📝 Manual</td><td style={S.td}>—</td><td style={S.td}>{T.manCap.toLocaleString()}</td><td style={S.td}>{T.manual.toLocaleString()}</td><td style={S.td}>{manFree.toLocaleString()}</td><td style={{ ...S.td, ...pctStyle(manPct) }}>{manPct}%</td><td style={{ ...S.td, fontSize: '9px' }}>Manual weekly entries</td></tr>
                    <tr style={{ fontWeight: 600, background: `${colors.primary}06` }}><td style={S.td}>TOTAL</td><td style={S.td}>{(T.rSmall + T.rLarge + T.floor + T.blocked + T.virt).toLocaleString()}</td><td style={S.td}>{T.tCap.toLocaleString()}</td><td style={S.td}>{T.tUsed.toLocaleString()}</td><td style={S.td}>{T.tFree.toLocaleString()}</td><td style={{ ...S.td, ...pctStyle(tPct) }}>{tPct}%</td><td style={S.td}></td></tr>
                  </>;
                })()}
              </tbody>
            </table>

            {/* Per Warehouse */}
            <h3 className="dcv-report-section-title">Per-Warehouse Detail</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
              <thead><tr>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Warehouse</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Total Bins</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Level 10</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Rack Cap</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Rack Used</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Rack %</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Floor Pallets</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Blocked</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Virtual</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Manual</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Total Cap</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Total Used</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Total Free</th>
                <th style={{ ...S.th, background: `${colors.primary}08` }}>Util %</th>
              </tr></thead>
              <tbody>
                {whSummaries.map(w => {
                  const rackPct = w.rackCap > 0 ? Math.round((w.rackUsed / w.rackCap) * 100) : 0;
                  return (
                  <tr key={w.warehouse}>
                    <td style={S.td}><span style={{ fontWeight: 600 }}>{w.warehouse}</span> <span style={{ fontSize: '8px', color: colors.textMuted }}>{w.displayName}</span></td>
                    <td style={S.td}>{(w.rackSmallBins + w.rackLargeBins + w.floorBulkBins + w.tempBlockedBins).toLocaleString()}</td>
                    <td style={{ ...S.td, fontWeight: 600, color: '#f59e0b' }}>{w.level10Count}</td>
                    <td style={{ ...S.td, color: colors.primary }}>{w.rackCap.toLocaleString()}</td>
                    <td style={{ ...S.td, color: '#f43f5e' }}>{w.rackUsed.toLocaleString()}</td>
                    <td style={{ ...S.td, fontWeight: 600, color: rackPct > 90 ? '#f43f5e' : rackPct > 70 ? '#f59e0b' : '#10b981' }}>{rackPct}%</td>
                    <td style={{ ...S.td, color: '#14b8a6' }}>{w.flPallets > 0 ? w.flPallets.toLocaleString() : '—'}</td>
                    <td style={{ ...S.td, color: '#f43f5e' }}>{w.blCap > 0 ? w.blCap.toLocaleString() : '—'}</td>
                    <td style={{ ...S.td, color: '#06b6d4' }}>{w.vUsed > 0 ? w.vUsed.toLocaleString() : '—'}</td>
                    <td style={{ ...S.td, color: '#8b5cf6' }}>{w.manPallets > 0 ? `${w.manPallets}/${w.manCap}` : '—'}</td>
                    <td style={{ ...S.td, fontWeight: 700, color: colors.primary }}>{w.tCap.toLocaleString()}</td>
                    <td style={{ ...S.td, fontWeight: 600, color: '#f43f5e' }}>{w.tUsed.toLocaleString()}</td>
                    <td style={{ ...S.td, color: '#10b981' }}>{w.tFree.toLocaleString()}</td>
                    <td style={S.td}><span style={{ fontWeight: 700, color: w.utilPct > 90 ? '#f43f5e' : w.utilPct > 70 ? '#f59e0b' : '#10b981' }}>{w.utilPct}%</span></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Capacity Rules */}
            <h3 className="dcv-report-section-title">Capacity Calculation Rules</h3>
            <div style={{ fontSize: '11px', color: colors.textSecondary, lineHeight: 1.6 }}>
              <p style={{ margin: '0 0 4px' }}>Segment Threshold: <strong>≤{threshold}</strong> segments = Rack Small, <strong>&gt;{threshold}</strong> segments = Rack Large, <strong>0</strong> segments = Floor/Bulk</p>
              <p style={{ margin: '0 0 4px' }}>Rack Small: Level 10 (ground) = <strong>2 pallets</strong> capacity | All other levels = <strong>1 pallet</strong> capacity</p>
              <p style={{ margin: '0 0 4px' }}>Rack Large: Capacity = <strong>actual segment count</strong> (TOTAL_NO_OF_SEGMENTS)</p>
              <p style={{ margin: '0 0 4px' }}>Temp Blocked: Full capacity counted as <strong>100% used</strong></p>
              <p style={{ margin: '0 0 4px' }}>Floor/Bulk: Non-racked storage — pallets tracked via IBM customer data</p>
              {T.ovr > 0 && <p style={{ margin: '0 0 4px', color: '#ec4899' }}>User Overrides: <strong>{T.ovr}</strong> bins reclassified with custom capacity</p>}
              {T.exc > 0 && <p style={{ margin: '0 0 4px', color: '#f43f5e' }}>Excluded Bins: <strong>{T.exc}</strong> bins excluded from all calculations</p>}
            </div>

            <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '9px', color: colors.textMuted, borderTop: `1px solid ${colors.border}`, paddingTop: '8px' }}>
              HalaRobo AI — Warehouse Management System | Report generated {fmtDate()} | Data: {rawWarehouseData.length} physical + {(rawVirtualData || []).length} virtual + {(customerStorageData || []).length} customer records + {(manualEntries || []).length} manual entries
            </div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      {viewMode !== 'report' && <div style={{ padding: '6px 10px', fontSize: '8px', color: colors.textMuted, textAlign: 'center' }}>
        {rawWarehouseData.length} physical + {(rawVirtualData || []).length} virtual + {(customerStorageData || []).length} customer + {(manualEntries || []).length} manual |
        Threshold ≤{threshold} | {T.ovr > 0 ? `${T.ovr} overrides | ` : ''}{T.exc > 0 ? `${T.exc} excluded` : ''}
      </div>}
    </div>
  );
};

export default DynamicCapacityVerification;
